import React, { useEffect, useState } from 'react';
import { TextField, Dialog, DialogTitle, DialogContent, DialogActions, InputAdornment, Button, Typography } from '@material-ui/core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import SearchIcon from '@material-ui/icons/Search';
import TrashIcon from '@material-ui/icons/Delete';
import SubmittableSearchBar from '../SubmittableSearchBar';
import LocationsTreeNode from './LocationsTreeNode';
import './LocationsPicker.scss';
import CustomToolTip from '../../../common/components/UI/ToolTip/ToolTip';


const createLocTrees = (nodes, selectedNodes) => {
    const nodesList = Object.values(nodes);
    const roots = [];

    nodesList.forEach(x => {
        x.children = x.childrenIds.map(y => nodes[y]);
        x.parentNode = nodes[x.parentNodeId];
        x.deepChildren = x.deepChildrenIds.map(y => nodes[y]);
        x.isExpanded = false;
        x.isChecked = false;
        x.isIndeterminate = false;
        x.isSelected = selectedNodes.includes(x.nodeId);
        x.searchHelper = x.orgPath.toLowerCase()
        x.isSearchHighlight = false;
        if (x.isRoot) {
            roots.push(x);
        }
    });

    nodesList.filter(x => x.isSelected)
        .forEach(x => {
            x.isChecked = true;
            checkAllDeepChildren(x, selectedNodes);
            recursiveCheckParents(x, selectedNodes);
        })

    return roots;
}

const removeNodeFromSelection = (node, selectedNodes) => {
    const index = selectedNodes.indexOf(node.nodeId);
    if (index > -1) {
        selectedNodes.splice(index, 1);
    }
    node.isSelected = false;
}

const removeJobNodesFromSelection = (nodes, selectedNodes) => {
    nodes.forEach(node => {
        const index = selectedNodes.indexOf(node.nodeId);
        if (index > -1) {
            selectedNodes.splice(index, 1);
        }
        node.isSelected = false;
    }

    );

}

const checkAllDeepChildren = (node, selectedNodes) => {
    if (!node.deepChildren || !node.deepChildren.length) {
        return;
    }

    node.deepChildren.forEach(x => {
        x.isChecked = true;
        if (x.isSelected) {
            removeNodeFromSelection(x, selectedNodes);
        }
    });
}

const uncheckAllDeepChildren = (node, selectedNodes) => {
    if (!node.deepChildren || !node.deepChildren.length) {
        return;
    }
    node.deepChildren.forEach(x => {
        x.isChecked = false;
        x.isIndeterminate = false;
        if (x.isSelected) {
            removeNodeFromSelection(x, selectedNodes);
        }
    });
}

const recursiveCheckParents = (node, selectedNodes) => {
    if (!node.parentNode) {
        return;
    }

    node.parentNode.isChecked = true;
    mergeChildrenIfAllChecked(node.parentNode, selectedNodes);
    if (isAllDeepChildrenChecked(node.parentNode)) {
        node.parentNode.isIndeterminate = false;
    } else {
        node.parentNode.isIndeterminate = true;
    }
    recursiveCheckParents(node.parentNode, selectedNodes);
}

const isAllDeepChildrenChecked = (node) => node.deepChildren.every(x => x.isChecked);
const isAllDeepChildrenSameCheckValue = (node) => node.deepChildren && node.deepChildren.length && node.deepChildren.reduce((previousValue, currentValue) => previousValue && currentValue.isChecked === node.deepChildren[0].isChecked, true);


const mergeChildrenIfAllChecked = (parentNode, selectedNodes) => {
    if (isAllDeepChildrenChecked(parentNode)) {
        parentNode.children.forEach(x => {
            if (!x.isJob) {
                removeNodeFromSelection(x, selectedNodes);
            }

        })
        parentNode.isSelected = true;
        if (parentNode.isJob) {
            selectedNodes.push(parentNode.nodeId);
        }
    } else {
        parentNode.isIndeterminate = true;
    }
}


const recursiveSelectAllCheckedSiblings = (node, selectedNodes) => {
    if (!node.parentNode) {
        return;
    }

    if (!node.parentNode.isSelected) {
        recursiveSelectAllCheckedSiblings(node.parentNode, selectedNodes);
    }
    removeNodeFromSelection(node.parentNode, selectedNodes);
    node.parentNode.children.forEach(x => {
        if (x.isChecked && x.nodeId !== node.nodeId) {
            x.isSelected = true;
            if (x.isJob) {
                selectedNodes.push(x.nodeId);
            }
            checkAllDeepChildren(x, selectedNodes);
        }
    });
}

const recursiveUncheckParents = (node) => {
    if (!node.parentNode) {
        return;
    }

    if (isAllDeepChildrenSameCheckValue(node.parentNode)) {
        if (node.isChecked) {
            node.parentNode.isChecked = true;
            node.parentNode.isIndeterminate = false;
        } else {
            node.parentNode.isChecked = false;
            node.parentNode.isIndeterminate = false;
        }
    } else {
        node.parentNode.isChecked = true;
        node.parentNode.isIndeterminate = true;
    }

    recursiveUncheckParents(node.parentNode);
}

const recursiveExpandParents = (node) => {
    if (!node.parentNode) {
        return;
    }

    // If already expanded, stop recursivity here!
    if (node.parentNode.isExpanded) {
        return;
    }

    node.parentNode.isExpanded = true;
    recursiveExpandParents(node.parentNode);
}

const getSelectedLocationLabel = (selection, dictionary, selectionText, locationNotFoundError) => {
    let locationLabel = "";

    if (selection.length === 1) {
        if (dictionary[selection[0]]) {
            locationLabel = dictionary[selection[0]].orgPath;
        } else {
            locationLabel = locationNotFoundError;
        }
    } else {
        locationLabel = `${selection.length} ${selectionText}`;
    }

    return locationLabel;
}

const getSelectedLocationsTooltip = (selection, dictionary, locationNotFoundError) => {
    const locationsTooltip = [];
    if (selection.length !== 1) {
        selection.forEach((element, index) => {
            if (dictionary[element]) {
                locationsTooltip.push(
                    <div key={`location-picker-selectd-tooltip-${element}-${index}`}>
                        {dictionary[element].orgPath}
                    </div>);
            } 
            else {
                locationsTooltip.push(
                    <div key={`location-picker-selectd-tooltip-not-found-${index}`}>
                        {locationNotFoundError}
                    </div>);
            }
        });
    } 
    return locationsTooltip;
}

/**  
 * @typedef {{
 *  nodeId: number;
 *  fullName: string;
 *  description: string;
 *  orgPath: string;
 *  childrenIds: number[];
 *  parentNodeId: number;
 *  parentNodeOrgPath: string;
 *  isJob: boolean;
 *  isRoot: boolean;
 * }} LocTreeNode  
 * 
 * @param {{
 *      period: Date | string;
 *      locationNodes: 
 *      selectedNodeIds: number[];
 * }} param0
 * @returns
 */
const LocationsPicker = ({ period, locationNodes = {}, selectedNodeIds = [], onConfirm = () => { }, t, disabled, singleSelection }) => {
    const [isShowModal, setIsShowModal] = useState(false);

    const [originalSelection, setOriginalSelection] = useState([...selectedNodeIds]);

    const [selectedNodes, setSelectedNodes] = useState(selectedNodeIds);
    const [locDictionary, setLocDictionary] = useState(locationNodes);

    const [locTrees, setLocTrees] = useState(null);

    useEffect(() => {
        setLocDictionary(locationNodes);

        setSelectedNodes([...new Set(selectedNodeIds)])
        setOriginalSelection([...new Set(selectedNodeIds)]);

        setLocTrees(createLocTrees(locationNodes, selectedNodes));

        selectedNodeIds.forEach(x => {
            if (locDictionary[x]) {
                recursiveExpandParents(locDictionary[x]);
            }
        });
    }, [JSON.stringify(Object.keys(locationNodes))]);


    const handleUpdateExpandable = (node) => {
        node.isExpanded = !node.isExpanded;
        setLocTrees([...locTrees]);
    }

    const handleSelectNode = (node) => {

        const newSelectedNodes = [...selectedNodes];
        if (singleSelection && selectedNodes.length > 0) {
            let oldNode = locationNodes[newSelectedNodes[0]];
            handleUnselectNode(oldNode);
            newSelectedNodes.splice(0, 1);
        }

        node.isChecked = true;
        node.isSelected = true;
        if (node.deepChildren.length > 0) {
            let jobNodeIds = node.deepChildren.filter(x => x.isJob).map(x => x.nodeId);
            newSelectedNodes.push(...jobNodeIds);
        }
        else {
            newSelectedNodes.push(node.nodeId)
        }


        singleSelection && selectedNodes.length > 0 ? checkAllDeepChildren(node, []) : checkAllDeepChildren(node, selectedNodes);
        recursiveCheckParents(node, newSelectedNodes);

        setSelectedNodes([...new Set(newSelectedNodes)]);
    }

    const handleUnselectNode = (node) => {
        const newSelectedNodes = [...selectedNodes];

        node.isChecked = false;
        node.isIndeterminate = false;
        if (!node.isSelected) {
            recursiveSelectAllCheckedSiblings(node, newSelectedNodes);
        }
        uncheckAllDeepChildren(node, newSelectedNodes);
        if (node.deepChildren.length > 0) {
            let jobNodes = node.deepChildren.filter(x => x.isJob);
            removeJobNodesFromSelection(jobNodes, newSelectedNodes);
            removeNodeFromSelection(node, newSelectedNodes);
        }
        else {
            removeNodeFromSelection(node, newSelectedNodes);
        }

        recursiveUncheckParents(node);

        setSelectedNodes([...new Set(newSelectedNodes)]);
    }

    const handleSearchChange = (searchString) => {
        searchString = searchString.toLowerCase().trim();
        const nodesList = Object.values(locDictionary);
        if (searchString === "") {
            nodesList.forEach(x => { x.isSearchHighlight = false; });
        } else {
            var subset = nodesList.filter(x => x.searchHelper.includes(searchString)).map(x => x.nodeId);
            nodesList.filter(x => !subset.includes(x.nodeId)).forEach(x => {
                x.isSearchHighlight = false;
                x.isExpanded = false;
            });
            nodesList.filter(x => subset.includes(x.nodeId)).forEach(x => {
                x.isSearchHighlight = true;
                x.isExpanded = true;
                recursiveExpandParents(x);
            });
        }
        setSelectedNodes([...selectedNodes]);
    }

    const handleCancel = () => {
        setIsShowModal(false);

        const selection = [...new Set(originalSelection)];
        setSelectedNodes(selection)

        setLocTrees(createLocTrees(locDictionary, selection));
        selectedNodeIds.forEach(x => {
            if (locDictionary[x]) {
                recursiveExpandParents(locDictionary[x]);
            }
        });
    }

    const handleConfirm = () => {
        onConfirm(selectedNodes);
        setOriginalSelection([...selectedNodes]);
        setIsShowModal(false);
    }

    var classTextField = !disabled ? "location-picker-text-field" : "";
    let tooltipText = getSelectedLocationsTooltip(originalSelection, locDictionary, t('locationPicker.error.locationNotFound'));
    const textField = (<TextField
            className={classTextField}
            label={t('locationPicker.formLabel')}
            data-toggle="tooltip"
            onClick={() => period && !disabled ? setIsShowModal(true) : null}
            disabled={!period || disabled}
            value={getSelectedLocationLabel(originalSelection, locDictionary, t('locationPicker.nbSelectedNodeLabel'), t('locationPicker.error.locationNotFound'))}
            fullWidth
            multiline
            variant="outlined"
            error={!period || originalSelection.length === 0}
            helperText={!period ? t('locationPicker.error.requiredPeriod') : (originalSelection.length === 0 ? t('locationPicker.error.requiredLocation') : null)}
            InputProps={{
                startAdornment: (
                    <InputAdornment position="start">
                        <FontAwesomeIcon icon={['fal', 'sitemap']} color='#357ebd'/>
                    </InputAdornment>
                )
            }}/>);

    const rendering = (
        <>
            {tooltipText.length ? <CustomToolTip titletext={<div>{tooltipText}</div>} jsx={textField} /> : textField}
            <Dialog
                open={isShowModal}
                onClose={handleCancel}
                className='location-picker-modal'
            >
                <DialogTitle
                    id="location-picker-header"
                    className='location-picker-header'
                    children={[
                        <span className='location-picker-title'>
                            {t('locationPicker.title')}
                        </span>,
                        <span>
                            {`${t('locationPicker.asOf')}: ${period}`}
                        </span>
                    ]}
                >

                </DialogTitle>
                <DialogContent>
                    <div className='location-picker-content'>
                        <div className='location-picker-tree'>
                            <div className='location-picker-search'>
                                <SubmittableSearchBar
                                    className="location-picker-search-bar"
                                    onChange={handleSearchChange}
                                    t={t}
                                />
                            </div>
                            {
                                locTrees && locTrees.map(root =>
                                    <LocationsTreeNode
                                        key={root.nodeId}
                                        isRoot={true}
                                        node={root}
                                        handleUpdateExpandable={handleUpdateExpandable}
                                        handleSelectNode={handleSelectNode}
                                        handleUnselectNode={handleUnselectNode}
                                        disabled={disabled}
                                        singleSelection={singleSelection}
                                    />
                                )
                            }
                        </div>
                        <div className='location-picker-selection'>
                            <Typography
                                className='location-picker-selection-list-title'
                                color="textSecondary"
                                variant="h5"
                            >
                                {t('locationPicker.selectionList')}
                            </Typography>

                            {
                                selectedNodes && selectedNodes.length ? selectedNodes.map(x => {
                                    const loc = locDictionary[x];
                                    if (loc) {
                                        return <div className="location-picker-selected-location" key={loc.nodeId}>
                                            {loc.orgPath}
                                            <div
                                                className='location-picker-selection-icon-container'
                                            >
                                                <SearchIcon
                                                    className='location-picker-selection-icon'
                                                    onClick={() => handleSearchChange(loc.orgPath)}
                                                />
                                                <TrashIcon
                                                    className='location-picker-selection-icon'
                                                    onClick={() => handleUnselectNode(loc)}
                                                />
                                            </div>
                                        </div>
                                    } else {
                                        return <div className="location-picker-selected-location" key={x}>{t('locationPicker.error.locationNotFound')}</div>;
                                    }
                                }) : null
                            }
                        </div>
                    </div>
                </DialogContent>
                <DialogActions>
                    <Button
                        key={'confirm'}
                        class="location-picker-footer location-picker-footer-confirm"
                        color="primary"
                        variant="contained"
                        onClick={handleConfirm}
                        disabled={disabled}
                    >
                        {t('locationPicker.confirm')}
                    </Button>
                    <Button
                        key={'cancel'}
                        class="location-picker-footer location-picker-footer-cancel"
                        color="secondary"
                        variant="contained"
                        onClick={handleCancel}
                    >
                        {t('locationPicker.cancel')}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );

    return rendering;
}

export default (LocationsPicker);
